import React, { useRef, useState, useEffect, useMemo } from "react";
import { Button } from "react-md";
import MGridForm from "components/Shared/MGridForm";
import { 
  DATA_TYPE_DROPDOWN,
  DATA_TYPE_STRING,
  DATA_TYPE_PHONE_NUMBER, 
  DATA_TYPE_DATE, 
  DATA_TYPE_TIME, 
  DATA_TYPE_MULTI_LINE_STRING 
} from "typeConstants/dataTypes";
import { contactPersonTypes, contactEffortResultTypes } from "typeConstants/contactEfforts";
import validator from "components/Shared/MGridFormDefinitions/validator";
import Radio from "@material-ui/core/Radio";
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import { ContactEffortsSettingsDto } from "interfaces/Mueller.Business.Models";

/** This is similar to ContactEffortsDto but uses strings for some ids to allow these to be used with select controls and adds contactTime property for separate date and time controls */
export interface CallLogEntryForm {
  contactEffortsId: Number;
  controlIdNumber: Number;
  phoneNumber: string;
  contactDate: string;
  contactTime: Date; 
  contactPersonTypeCode: string;
  spokeWith: string;
  resultCode: string;
  notes: string;
}

interface AddEditCallLogDialogProps {
  /** Call log data to edit */
  data: CallLogEntryForm;
  /** Function to call to hide the dialog */
  onCancel: () => void;
  /** Function to call when add/edit finished that will be passed the form data */
  onSave: (data: CallLogEntryForm) => void;
  /** Call log settings */
  settings: ContactEffortsSettingsDto;
}

enum EntryType {
  PHONE = "PHONE",
  EMAIL = "EMAIL",
}

const getInitialEntryType = (props: AddEditCallLogDialogProps) => props?.data?.phoneNumber && props?.data?.phoneNumber?.indexOf("@") >= 0 ? EntryType.EMAIL : EntryType.PHONE;

const AddEditCallLogDialog = (props: AddEditCallLogDialogProps) => {
  const [callLogData, setCallLogData] = useState<CallLogEntryForm>(props.data);
  const [entryType, setEntryType] = useState<string>(getInitialEntryType(props));
  const [personType, setPersonType] = useState<string>(null);
  const [availResultTypes, setAvailResultTypes] = useState([]);
  const formControlMethods = useRef({});  // TODO Add type when MGridForm has interfaces

  useEffect(() => {
    if (JSON.stringify(props.data) !== JSON.stringify(callLogData)) {
      handleFormChange(props.data);
      setEntryType(getInitialEntryType(props));
    }
  }, [props.data]);

  const handleSave = () => {
    if (formControlMethods.current.isFormValid()) {
      props.onSave(callLogData);
    }
  };

  const handleFormChange = (updatedObject: CallLogEntryForm) => {
    if(!props.settings.personTypes.length){
      setAvailResultTypes(contactEffortResultTypes);
    } else {
      let newPersonType = updatedObject.contactPersonTypeCode;
      if(newPersonType && newPersonType !== personType){
        let availResults = props.settings.personTypes.filter(s => s.key === newPersonType)[0]?.possibleResultIDs;
        let resultTypes = contactEffortResultTypes.filter(s => availResults.some(p => p === s.value));
        setAvailResultTypes(resultTypes);        
        setPersonType(newPersonType);
      }
    }
    setCallLogData({ ...updatedObject });
  };

  const formDefinition = useMemo(() => {
    let mGridFormDefinition = [
      [
        { dataType: DATA_TYPE_DATE, label: "Date of Contact", source: "contactDate", hasValidationError: validator.required }
      ],
      [
        { dataType: DATA_TYPE_TIME, label: "Time of Contact", source: "contactTime", hasValidationError: validator.required }
      ],
    ];

    if (entryType === EntryType.PHONE) {
      mGridFormDefinition.push(
        [
          { dataType: DATA_TYPE_PHONE_NUMBER, label: "Number contacted", source: "phoneNumber", hasValidationError: validator.combineValidators(validator.required, validator.phoneNumber) }
        ]);
    } else {
      mGridFormDefinition.push( 
        [ // Note that we still use the phoneNumber field to store email address
          { dataType: DATA_TYPE_STRING, label: "Email contacted", source: "phoneNumber", hasValidationError: validator.combineValidators(validator.required, validator.emailAddress) }
        ]);
    }

    mGridFormDefinition.push(
      [
        { dataType: DATA_TYPE_DROPDOWN, label: "Contact person", source: "contactPersonTypeCode", dataTypeOptions: { options: contactPersonTypes }, hasValidationError: validator.required }
      ],
      [
        { dataType: DATA_TYPE_STRING, label: "Spoke with", source: "spokeWith", hasValidationError: validator.required }
      ],
      [
        { dataType: DATA_TYPE_DROPDOWN, label: "Result", source: "resultCode", dataTypeOptions: { options: availResultTypes }, hasValidationError: validator.required }
      ],
      [
        { dataType: DATA_TYPE_MULTI_LINE_STRING, label: "Notes", source: "notes" }
      ],
    );

    return mGridFormDefinition;
  }, [entryType, personType]); 

  return (
    <>
      <div>Select Contact Method:</div>
      <FormControl component="fieldset">
        <RadioGroup 
          aria-label="Contact method" 
          name="contactMethod" 
          value={entryType} 
          onChange={e => setEntryType(e.target.value)}>
            <FormControlLabel value={EntryType.PHONE} control={<Radio />} label="Phone" />
            <FormControlLabel value={EntryType.EMAIL} control={<Radio />} label="Email" />
        </RadioGroup>
      </FormControl>
      <MGridForm
        width={500}
        data={callLogData}
        onChange={handleFormChange}
        formDefinition={formDefinition}
        portalTarget={() => document.querySelector(".add-edit-call-log-dialog")}
        getControlMethods={cm => { formControlMethods.current = cm; }}
        webform
      />    
      <div className="button-container">
        <Button key={"call-log-cancel-delete"} raised onClick={props.onCancel} >Cancel</Button>
        <Button key={"call-log-confirm-delete"} primary raised onClick={handleSave} >Save</Button>
      </div>      
    </>
  );
};

export default AddEditCallLogDialog;